import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformCircularMove } from "../../../transforms/transform_circular_move.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { Transformable } from "../../../transforms/transformable.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

export class LeaderboardInfoOverlay extends AbstractOverlay
{
  private static readonly TITLE_GRADIENT_TOP = "#FBF2A7";
  private static readonly TITLE_GRADIENT_BOTTOM = "#B84C42";
  private static readonly STAGGER_MS = 56;
  private static readonly BASE_DELAY_MS = 160;
  private static readonly ITEM_ANIM_DURATION_MS = 256;
  private static readonly ARROW_ANIM_DURATION_MS = 280;
  private static readonly TITLE_ANIM_DURATION_MS = 320;

  private content_holder: UIElementHolder | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: any,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );
    this.createElements();
  }

  private createElements(): void
  {
    const base_now = performance.now() + LeaderboardInfoOverlay.BASE_DELAY_MS;
    const stagger = LeaderboardInfoOverlay.STAGGER_MS;

    const addAppearTransform = ( el: Transformable, start_time: number ): void =>
    {
      el.setOpacity( 0 );
      el.setScale( new Vector2( 0.6, 0.6 ) );
      el.addTransform( new TransformOpacity( el, true, 0, 1, start_time, LeaderboardInfoOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
      el.addTransform( new TransformScale( el, true, null, new Vector2( 1, 1 ), start_time, LeaderboardInfoOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    };

    this.createCenteredHolder();
    this.createArrowTop( base_now + stagger * 4 );
    this.createArrowBottom( base_now + stagger * 1 );
    this.createTopPart( base_now, stagger, addAppearTransform );
    this.createCenterPart( base_now, stagger, addAppearTransform );
    this.createBottomPart( base_now, stagger, addAppearTransform );
    this.createTitle();
    this.createCloseText( base_now + stagger * 9 );
  }

  private static arcParams(
    from: Vector2,
    to: Vector2,
    bulge: number
  ): { center: Vector2; radius: number; start_angle: number; end_angle: number }
  {
    const mid     = new Vector2( ( from.x + to.x ) / 2, ( from.y + to.y ) / 2 );
    const dx      = to.x - from.x;
    const dy      = to.y - from.y;
    const len     = Math.sqrt( dx * dx + dy * dy );
    const perp_x  = -dy / len;
    const perp_y  =  dx / len;
    const center  = new Vector2( mid.x + perp_x * bulge, mid.y + perp_y * bulge );
    const radius      = Math.sqrt( ( from.x - center.x ) ** 2 + ( from.y - center.y ) ** 2 );
    const start_angle = Math.atan2( from.y - center.y, from.x - center.x );
    let   diff        = Math.atan2( to.y   - center.y, to.x   - center.x ) - start_angle;
    while ( diff >  Math.PI ) diff -= 2 * Math.PI;
    while ( diff < -Math.PI ) diff += 2 * Math.PI;
    const end_angle = start_angle + diff;
    return { center, radius, start_angle, end_angle };
  }

  private createCenteredHolder(): void
  {
    const holder_w_design = 982;
    const holder_h_design = 1242;

    const holder_w = LayoutScaler.widthFromDesign( holder_w_design );
    const holder_h = LayoutScaler.heightFromDesign( holder_h_design );
    const holder_x = this.rect.getW2() - holder_w / 2;
    const holder_y = this.rect.getH2() - holder_h / 2;

    this.content_holder = new UIElementHolder(
      this.ctx,
      holder_x,
      holder_y,
      holder_w,
      holder_h
    );

    this.addChild( this.content_holder );
  }

  private createTopPart( base_now: number, stagger: number, addAppearTransform: ( el: Transformable, start_time: number ) => void ): void
  {
    if ( !this.content_holder ) return;

    const top_part_w_design = 338;
    const top_part_h_design = 341;
    const top_part_x_design = 588;

    const top_part_w = LayoutScaler.widthFromDesign( top_part_w_design );
    const top_part_h = LayoutScaler.heightFromDesign( top_part_h_design );
    const top_part_x = LayoutScaler.xFromDesign( top_part_x_design );
    const top_part_y = 0;

    const top_part = new UIImage(
      this.ctx,
      top_part_x,
      top_part_y,
      top_part_w,
      top_part_h,
      ImgID.UI_LeaderboardInfoTopPart
    );
    addAppearTransform( top_part, base_now );

    const top_part_text_w = LayoutScaler.widthFromDesign( 545 );
    const top_part_text_h = top_part_h;
    const top_part_text_x = LayoutScaler.xFromDesign( 0 );
    const top_part_text_y = 0;

    const top_part_text = new UITextElement(
      this.ctx,
      top_part_text_x,
      top_part_text_y,
      top_part_text_w,
      top_part_text_h,
      {
        localization_key: "LEADERBOARD.TOP_TEXT",
        text_color: ColorScheme.white,
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Right,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 1.17,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    addAppearTransform( top_part_text, base_now + stagger );
    this.content_holder.addChild( top_part_text );
    this.content_holder.addChild( top_part );
  }

  private createCenterPart( base_now: number, stagger: number, addAppearTransform: ( el: Transformable, start_time: number ) => void ): void
  {
    if ( !this.content_holder ) return;

    const center_part_y_design = 370;
    const center_part_w_design = 306;
    const center_part_h_design = 309;

    const center_part_w = LayoutScaler.widthFromDesign( center_part_w_design );
    const center_part_h = LayoutScaler.heightFromDesign( center_part_h_design );
    const center_part_x = 0;
    const center_part_y = LayoutScaler.yFromDesign( center_part_y_design );

    const center_part = new UIImage(
      this.ctx,
      center_part_x,
      center_part_y,
      center_part_w,
      center_part_h,
      ImgID.UI_LeaderboardInfoCenterPart
    );
    addAppearTransform( center_part, base_now + stagger * 3 );
    this.content_holder.addChild( center_part );

    const center_part_text_w = LayoutScaler.widthFromDesign( 531 );
    const center_part_text_h = LayoutScaler.heightFromDesign( 234 );
    const center_part_text_x = LayoutScaler.xFromDesign( 360 );
    const center_part_text_y = center_part_y + LayoutScaler.yFromDesign( 66 );

    const center_part_text = new UITextElement(
      this.ctx,
      center_part_text_x,
      center_part_text_y,
      center_part_text_w,
      center_part_text_h,
      {
        localization_key: "LEADERBOARD.CENTER_TEXT",
        text_color: ColorScheme.white,
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 1.17,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    addAppearTransform( center_part_text, base_now + stagger * 4 );
    this.content_holder.addChild( center_part_text );
  }

  private createBottomPart( base_now: number, stagger: number, addAppearTransform: ( el: Transformable, start_time: number ) => void ): void
  {
    if ( !this.content_holder ) return;

    const bottom_part_w_design = 679;
    const bottom_part_h_design = 252;
    const bottom_part_x_design = 152;
    const bottom_part_y_design = 870;

    const bottom_part_w = LayoutScaler.widthFromDesign( bottom_part_w_design );
    const bottom_part_h = LayoutScaler.heightFromDesign( bottom_part_h_design );
    const bottom_part_x = LayoutScaler.xFromDesign( bottom_part_x_design );
    const bottom_part_y = LayoutScaler.yFromDesign( bottom_part_y_design );

    const bottom_part = new UIImage(
      this.ctx,
      bottom_part_x,
      bottom_part_y,
      bottom_part_w,
      bottom_part_h,
      ImgID.UI_LeaderboardInfoBottomPart
    );
    addAppearTransform( bottom_part, base_now + stagger * 6 );
    this.content_holder.addChild( bottom_part );

    const bottom_part_text_y = LayoutScaler.yFromDesign( 1131 );
    const bottom_part_text = new UITextElement(
      this.ctx,
      bottom_part_x,
      bottom_part_text_y,
      bottom_part_w,
      bottom_part_h,
      {
        localization_key: "LEADERBOARD.BOTTOM_TEXT",
        text_color: ColorScheme.white,
        font_size: LayoutScaler.textSizeFromDesign( 38 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 1.1,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    addAppearTransform( bottom_part_text, base_now + stagger * 7 );
    this.content_holder.addChild( bottom_part_text );
  }

  private createArrowTop( start_time: number ): void
  {
    if ( !this.content_holder ) return;

    const arrow_x_design = 202;
    const arrow_w_design = 168;
    const arrow_h_design = 155;
    const arrow_y_design = 717;
    const rotation_deg_from = 85;
    const rotation_deg_to = 60;

    const arrow_w = LayoutScaler.widthFromDesign( arrow_w_design );
    const arrow_h = LayoutScaler.heightFromDesign( arrow_h_design );
    const arrow_x = LayoutScaler.xFromDesign( arrow_x_design );
    const arrow_y = LayoutScaler.yFromDesign( arrow_y_design );
    const arrow_start_x = LayoutScaler.widthFromDesign( 180 ) / 2;
    const arrow_start_y = LayoutScaler.yFromDesign( 360 ) + LayoutScaler.heightFromDesign( 324 ) / 2;

    const from_rad = rotation_deg_from * Math.PI / 180;
    const to_rad = rotation_deg_to * Math.PI / 180;

    const arrow = new UIImage(
      this.ctx,
      arrow_start_x,
      arrow_start_y,
      arrow_w,
      arrow_h,
      ImgID.UI_DailyTasksArrow
    );
    arrow.setOpacity( 0 );
    arrow.setRotation( from_rad );

    const arc_top = LeaderboardInfoOverlay.arcParams(
      new Vector2( arrow_start_x, arrow_start_y ),
      new Vector2( arrow_x, arrow_y ),
      LayoutScaler.widthFromDesign( -120 )
    );

    arrow.addTransform( new TransformOpacity( arrow, true, 0, 1, start_time, LeaderboardInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformRotate( arrow, true, from_rad, to_rad, start_time, LeaderboardInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformCircularMove( arrow, true, arc_top.center, arc_top.radius, start_time, LeaderboardInfoOverlay.ARROW_ANIM_DURATION_MS, arc_top.start_angle, arc_top.end_angle, { easing: Easing.EaseOutQuad } ) );

    this.content_holder.addChild( arrow );
  }

  private createArrowBottom( start_time: number ): void
  {
    if ( !this.content_holder ) return;

    const arrow_x_design = 546;
    const arrow_w_design = 168;
    const arrow_h_design = 155;
    const arrow_y_design = 309;
    const rotation_deg_from = 182;
    const rotation_deg_to = 137;

    const arrow_w = LayoutScaler.widthFromDesign( arrow_w_design );
    const arrow_h = LayoutScaler.heightFromDesign( arrow_h_design );
    const arrow_x = LayoutScaler.xFromDesign( arrow_x_design );
    const arrow_y = LayoutScaler.yFromDesign( arrow_y_design );
    const arrow_start_x = LayoutScaler.xFromDesign( 588 ) + LayoutScaler.widthFromDesign( 303 ) / 2;
    const arrow_start_y = LayoutScaler.heightFromDesign( 324 ) / 2;

    const from_rad = rotation_deg_from * Math.PI / 180;
    const to_rad = rotation_deg_to * Math.PI / 180;

    const arrow = new UIImage(
      this.ctx,
      arrow_start_x,
      arrow_start_y,
      arrow_w,
      arrow_h,
      ImgID.UI_DailyTasksArrow,
      { direction_x: -1, direction_y: 1 }
    );
    arrow.setOpacity( 0 );
    arrow.setRotation( from_rad );

    const arc_bottom = LeaderboardInfoOverlay.arcParams(
      new Vector2( arrow_start_x, arrow_start_y ),
      new Vector2( arrow_x, arrow_y ),
      LayoutScaler.widthFromDesign( 120 )
    );

    arrow.addTransform( new TransformOpacity( arrow, true, 0, 1, start_time, LeaderboardInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformRotate( arrow, true, from_rad, to_rad, start_time, LeaderboardInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformCircularMove( arrow, true, arc_bottom.center, arc_bottom.radius, start_time, LeaderboardInfoOverlay.ARROW_ANIM_DURATION_MS, arc_bottom.start_angle, arc_bottom.end_angle, { easing: Easing.EaseOutQuad } ) );

    this.content_holder.addChild( arrow );
  }

  private createTitle(): void
  {
    if ( !this.content_holder ) return;

    const crown_w_design = 93;
    const title_h_design = 128;
    const title_y_design = 94;
    const title_text_size_design = 60;

    const crown_w = LayoutScaler.widthFromDesign( crown_w_design );
    const title_w = this.rect.getW();
    const title_h = LayoutScaler.heightFromDesign( title_h_design );
    const title_x = this.rect.getW2() - title_w / 2;
    const title_y = LayoutScaler.yFromDesign( title_y_design );
    const text_size = LayoutScaler.textSizeFromDesign( title_text_size_design );
    const title_offset_y = LayoutScaler.yFromDesign( 20 );
    const now = performance.now();

    const title = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        localization_key: "LEADERBOARD",
        font_size: text_size,
        font_weight: 900,
        text_color: ColorScheme.white,
        gradient_text_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#FFFFFF" },
            { offset: 1, color: "#FFDE8D" },
          ],
        },
        text_style: TextStyleScheme.rubik,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: "#05378B",
        shadow_color: "#C75C00",
        shadow_blur: LayoutScaler.widthFromDesign( 2 ),
        shadow_offset_x: LayoutScaler.xFromDesign( 0 ),
        shadow_offset_y: LayoutScaler.yFromDesign( 5 ),
        auto_wrap: false,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 42 ),
      }
    );

    title.setOpacity( 0 );
    title.setPixelPos( new Vector2( title_x, title_y + title_offset_y ), true );
    title.addTransform( new TransformOpacity( title, true, 0, 1, now, LeaderboardInfoOverlay.TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    title.addTransform( new TransformStraightMove( title, true, null, new Vector2( title_x, title_y ), now, LeaderboardInfoOverlay.TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );

    this.addChild( title );
  }

  private createCloseText( start_time: number ): void
  {
    const size = LayoutScaler.widthFromDesign( 110 );
    const bottom_margin = LayoutScaler.yFromDesign( 95 );
    const close = new UIImage(
      this.ctx,
      this.rect.getW2() - size / 2,
      this.rect.getH() - bottom_margin - size,
      size,
      size,
      ImgID.UI_DailyTasksCloseBtn,
      undefined,
      true
    );
    close.setOpacity( 0 );
    close.addTransform( new TransformOpacity( close, true, 0, 1, start_time, LeaderboardInfoOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    close.setOnClick( () => OverlayManager.closeOverlay() );
    this.addChild( close );
  }

  public getDimOpacity(): number
  {
    return 0.85;
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    super.onTouchStart( p, now );
    OverlayManager.closeOverlay();
    return true;
  }

  public update( now: number ): void
  {
    super.update( now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "LeaderboardInfoOverlay";
  }
}
